import { useContext, useCallback, useMemo, useRef } from 'react';
import { ZoneContext } from 'context/ZoneContext';
import { IconWrapper, StationWrapper } from '../../../Station.styled';
import StationIcon from 'components/common/StationIcon';
import { SubZoneStation } from '..';
import { Description } from '../../Description';
import colors from 'theme/colors';
import { CloseIcon, SubZoneBackground, SubZoneBackgroundWrapper, SubZoneTimelineWrapper } from './SubZoneExpandedView.styled';
import { PADDING_AROUND_SUBZONE_CHILDREN } from '../SubZoneStation';
import { StationStatus } from 'pages/Map/types';
import { calculateSubZoneBoundaries } from './utils';
import { useExpandedStationOutsideClickAlerter } from '../../../utils';
import { MapTourContext } from 'context/MapTourContext';
import { TourType } from 'pages/Map/components/MapTour';
import useMapSearch from 'hooks/search/useMapSearch';

const ADDITIONAL_SPACE_ON_TOP = 30;
const MOVE_SUBZONE_WRAPPER_BY = ADDITIONAL_SPACE_ON_TOP + 5;
type SubZoneProps = { station: SubZoneStation };

/**
 * For expanded subzone we need to draw container around all its child stations and paths
 * this container calculates the width & height based on its children positions and path points
 *  - renders with additional width (additional width is to have extra space around stations along with description / timeline info)
 *  - and with background to represent it as subzone
 * when we create this container with additional width we need to move it left accordingly otherwise extra space is added only to the right of the stations
 *  - no description on left side of stations, so moving accordingly
 */
export const SubZoneExpandedView = ({ station }: SubZoneProps) => {
    const {
        setSelectedStation,
        activeStation,
        stations,
        paths,
        setExpandedStation,
        mapState,
        expandedDescriptionStation,
        setExpandedDescriptionStation,
        selectedStation
    } = useContext(ZoneContext);
    const stationStatus = station.getStationStatus(activeStation, paths, selectedStation);
    const stationColor = station.getStationColor(stationStatus);
    const isDescriptionExpanded = expandedDescriptionStation?.id === station.id;
    const defaultZIndex = station.getDefaultZIndex(activeStation);
    const stationZIndex = isDescriptionExpanded ? station.getHoveredZIndex(defaultZIndex) : defaultZIndex;
    const boundaries = useMemo(() => calculateSubZoneBoundaries(station, stations, paths), [station, stations, paths]);
    const { isCurrentTourItem } = useContext(MapTourContext);
    const { getSearchColor } = useMapSearch();

    const onDescriptionExpand = useCallback(() => {
        setExpandedDescriptionStation(station);
    }, []);

    const onDescriptionCollapse = useCallback(() => {
        setExpandedDescriptionStation(undefined);
    }, []);

    const onStationClick = useCallback(() => {
        setSelectedStation(station);
    }, [station, setSelectedStation]);

    const onClose = useCallback(() => {
        setExpandedStation(undefined);
    }, [setExpandedStation]);

    const startTime = station.getStartTime();
    const endTime = station.getEndTime();
    const subZonePosition = station.getAbsolutePosition();
    const style = {
        left: subZonePosition.x,
        top: subZonePosition.y,
        backgroundColor: stationColor,
        zIndex: stationZIndex
    };

    const { topLeft, bottomRight } = boundaries;
    const width = bottomRight.x - topLeft.x + PADDING_AROUND_SUBZONE_CHILDREN;
    const height = bottomRight.y - topLeft.y + ADDITIONAL_SPACE_ON_TOP;

    const ref = useRef(null);
    useExpandedStationOutsideClickAlerter(ref, onClose, mapState, {
        exceptionEleIds: 'path-selection-modal',
        exceptionEleClass: 'station-expand-trigger-element'
    });
    const backgroundColor = getSearchColor(station.id) || station.getStationColor(stationStatus);

    return (
        <>
            <StationWrapper style={style} id={station.id} isMapTourActiveElement={isCurrentTourItem(TourType.INFO_ABOUT_STATIONS)}>
                <IconWrapper
                    onClick={onStationClick}
                    onMouseEnter={onDescriptionExpand}
                    onMouseLeave={onDescriptionCollapse}
                    style={{ backgroundColor }}
                    isPathSelectedInTour={isCurrentTourItem(TourType.INFO_ABOUT_PATHS)}
                >
                    <StationIcon
                        type={station.getType()}
                        isHighlight={isDescriptionExpanded || station.isSelectedStation(selectedStation)}
                    />
                </IconWrapper>
                <Description
                    onDescriptionExpand={onDescriptionExpand}
                    onDescriptionCollapse={onDescriptionCollapse}
                    station={station}
                    isActiveStation={stationStatus === StationStatus.ACTIVE_STATION}
                    onClick={onStationClick}
                />
            </StationWrapper>
            <SubZoneBackgroundWrapper ref={ref} style={{ left: topLeft.x, top: style.top - MOVE_SUBZONE_WRAPPER_BY, height, width }}>
                <CloseIcon
                    data-testid="subzone-close"
                    onClick={onClose}
                    size="XS"
                    variant="solid"
                    iconColor={colors.white}
                    bgColor={colors.darkBlue}
                />
                <SubZoneBackground xPosition={subZonePosition.x - topLeft.x}>
                    {endTime ? (
                        <>
                            <SubZoneTimelineWrapper textVariant="body3" style={{ transform: 'translate(-5px, -15px)' }}>
                                {startTime}
                            </SubZoneTimelineWrapper>
                            <SubZoneTimelineWrapper textVariant="body3" style={{ transform: 'translate(-5px, 15px)', bottom: 0 }}>
                                {endTime}
                            </SubZoneTimelineWrapper>
                        </>
                    ) : null}
                </SubZoneBackground>
            </SubZoneBackgroundWrapper>
        </>
    );
};
